/**
 * @file Metadata helpers for typed detail routes.
 */

import {
    getBoardRegistryEntries,
    getBoardItemTypeForCollectionName,
    getBoardRegistryEntryByItemType,
    isBoardDetailRouteEnabled,
} from "../../shared/board/boardRegistry.js";

function buildDetailRouteCollections() {
    return getBoardRegistryEntries().reduce((collections, board) => {
        if (!isBoardDetailRouteEnabled(board.viewKey)) {
            return collections;
        }
        if (board?.collectionName && board?.itemType) {
            collections[board.collectionName] = board.itemType;
        }
        return collections;
    }, {});
}

const DETAIL_ROUTE_COLLECTION_MAP = Object.freeze(buildDetailRouteCollections());

/**
 * Returns the collection-to-item-type map for supported detail routes.
 * @returns {Object<string, string>} Detail route collections keyed by collection.
 */
export function getDetailRouteCollections() {
    return { ...DETAIL_ROUTE_COLLECTION_MAP };
}

/**
 * Resolves the collection name for a typed detail item.
 * @param {string} itemType - Singular item type.
 * @returns {string|null} Collection name, or null for unsupported item types.
 */
export function getDetailRouteCollectionForItemType(itemType) {
    const board = getBoardRegistryEntryByItemType(itemType);
    return board && isBoardDetailRouteEnabled(board.viewKey) ?
        board.collectionName :
        null;
}

/**
 * Resolves the singular item type for a detail route collection.
 * @param {string} collectionName - Collection segment from the route.
 * @returns {string|null} Item type, or null for unsupported collections.
 */
export function getDetailRouteItemTypeForCollection(collectionName) {
    const itemType = getBoardItemTypeForCollectionName(collectionName);
    return getDetailRouteCollectionForItemType(itemType) === collectionName ?
        itemType :
        null;
}
